import React, {
  useCallback, useEffect, useId, useMemo, useRef, useState,
} from 'react';
import { useNavigate } from 'react-router-dom';

import ConfirmationModal from '../../common/ConfirmationModal/ConfirmationModal';
import HipRecordRows, { ROW_CAP, isTickable, replaceLockReason } from './HipRecordRows';
import { pausedWhenIST } from './slotTime';
import HipRecordTypeStrip from './HipRecordTypeStrip';
import HipRecordPreviewModal from './HipRecordPreviewModal';
import HipInlineAddFile from './HipInlineAddFile';
import HipInvoiceDrawer from './HipInvoiceDrawer';
import HipPractitionerPicker from './HipPractitionerPicker';
import { usePermission } from '../../../context/PermissionContext';
import { linkRecords, uploadRecordFiles } from '../../../services/api/hipRecordsApi';
import { deleteInvoice } from '../../../services/api/hipInvoicesApi';
import { ROUTES } from '../../../constants/routes';
import { ABDM_ICONS, LINK_COPY } from '../../../constants/abdmCopy';
import {
  HI_TYPES, HI_TYPE_ICONS, TYPE_INVOICE, hiTypeLabel,
} from '../../../constants/abdmHiTypes';
import {
  CARD, CONFIRM, INLINE, INVOICE, LIST, PATIENT, PRACTITIONER, REPLACE, ROW, ROWS, SLOT_STATE_LABEL, UPLOAD,
  encounterPhrase, incompleteRecordText, slotName,
} from '../../../constants/hipRecordsCopy';
import {
  API_MODE, ENCOUNTER_KIND, SLOT_STATE, SLOT_BLOCKED_REASON, ABHA_STATUS, PRACTITIONER_REQUIRED, isBlocked, isEmptyRecord, isNotLinkedYet,
} from '../../../constants/hipRecordEnums';
import {
  ACCEPT_ATTR, BANNER, FILE_REJECTED, PHASE, UPLOAD_ERROR_CODES, addPickedFiles, draftTitle, isSingleFile, needsDoctor, newDraft,
} from './hipUploadFiles';
import { ADMISSION_ACCESS_ERROR_CODE, OUT_OF_DEPARTMENT_COPY_OTHER_ROLES } from '../../../constants/admission';
import { formatDateIST, formatDayIST, formatDateTimeISTWithYearNoSeconds } from '../../../utils/formatIST';
import { formatToken } from '../../../utils/formatToken';

/**
 * HipEncounterCard - one OPD visit / IPD admission on "Link health records to ABHA" (redesign v2, Variant A,
 * OR-65/67/73/75): header (patient name link, "View all records for this patient →", meta line, ONE state pill),
 * the 8-pill record-type strip, the checklist rows (tick boxes) and ONE primary "Link N records to ABHA".
 * Props: { record, onChanged(linkInfo?), inPatientView?, listFilters? }
 *   record       an EncounterRow from hipRecordsApi.listRecords
 *   onChanged    called ONCE after every write so the list refetches; after a link that linked something - from the
 *                main button, the inline Add file panel or the invoice form (OR-77) - it gets { patientName, records }
 *                (the list's link notice, OR-93 / OR-114): records = [{ encounterKind, encounterId, patientId, slotKey (an invoice:
 *                invoiceId instead), name (as the rows show it, slotName), state (the slot state the server answered, LINKING
 *                for an accepted link) }] - the card stamps its own encounterKind / encounterId on every path (`changed`), so
 *                the list can find each record again in a refetch. After any other write no argument
 *   inPatientView the card sits on the patient page: no "View all records" link (it would point at this page)
 *   listFilters  the list's filter words, handed to the patient page as router state (PATIENT.filtersKept)
 *
 * Selection (INV-S): the ticked set is DERIVED, never stored: ticked = tickable slots (isTickable: canLink, not an
 * Invoice, not blocked) minus `unticked` (a Set of slotKey the user cleared). A refetch that adds a linkable slot
 * ticks it by default; a slot that stops being linkable drops out by itself, so it is never sent. The button's N is
 * the length of the list that is sent. Rows hidden behind "Show n more" stay ticked.
 *
 * A slot is identified by `slotKey` (A1.1), never by record type. Every per-slot state here (applied writes, link
 * errors, view, Replace target) is keyed by slotKey.
 *
 * Add file / Replace (OR-82): no drawer. A missing pill (or the "+" of a Diagnostic Report) opens the OS file picker at
 * once - from ONE hidden file input in the card, no `capture`, no camera - and the chosen file(s) open the inline panel
 * (HipInlineAddFile) under the strip. The pencil of the preview does the same for Replace. ONE draft (= one open panel)
 * per card: while it is open the other add pills and the pencil say "Finish or cancel the file you are adding first.".
 * The draft lives here, so it survives a list refetch. "Upload and link to ABHA" = confirm, upload (ADD), then ONE link
 * of the slot the upload returned (by slotKey): that link reports its record through onChanged like the main button does.
 * Replace only swaps the file (onChanged() with no argument). A link that fails after a good upload
 * closes the panel into the saved row and says so in a card line; the main button retries it.
 *
 * Invoices never tick (OR-70): a row's "Open bill" / "Fill in bill →" opens the invoice form, where it is linked;
 * a frozen invoice whose link failed is retried there too ("Retry link", one linkInvoice). A draft is deleted from
 * its row's trash icon (ConfirmationModal, one request).
 *
 * A visit with no doctor (`record.needsPractitioner`, OR-59): a confirm holding any NEW link asks for a required
 * "Doctor for patient" and sends it as `practitionerId`. A RETRY (R3: a failed slot / one with a care context) never
 * asks and sends none - the server keeps the stored pick; only a 422 PRACTITIONER_REQUIRED (nothing stored) opens the
 * confirm with the picker. When every ticked row is a retry the button says "Try again" and links at once.
 *
 * Writes: the returned slot / result is applied to the card at once, then onChanged() fires ONCE. Applied slots are
 * tied to the `record` they were applied on - a refetch hands in a new record and the server's truth replaces them.
 * The card never fetches on mount; ticking makes no request.
 */

// OR-82 open point 1 (default: keep): "Upload and link to ABHA" still asks first ("This can't be undone."). false = upload and link at once.
const ASK_BEFORE_UPLOAD_AND_LINK = true;

const LINK_BLOCKED = 'LINK_BLOCKED';
const INCOMPLETE_RECORD = 'INCOMPLETE_RECORD';
const NO_PATCH = Object.freeze({});
const NO_SUGGESTIONS = Object.freeze([]);
const NO_KEYS = new Set();

const PILL = 'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-bold tracking-wide whitespace-nowrap';
const PILL_TONE = {
  info: 'bg-sky-100 text-sky-800 dark:bg-sky-900 dark:text-sky-200',
  ok: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  warn: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  bad: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
};
// The ONE primary action (solid fill, exempt from the outline rule) + the plain text links (tinted, on a solid card).
const BTN_PRIMARY = 'w-full rounded-lg border border-teal-700 bg-teal-700 px-4 py-2 text-sm font-bold text-white hover:bg-teal-800 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto dark:border-teal-500 dark:bg-teal-700 dark:hover:bg-teal-600';
const LINK = 'p-0 text-xs font-semibold text-teal-700 underline hover:text-teal-900 disabled:cursor-not-allowed disabled:opacity-60 dark:text-teal-300 dark:hover:text-teal-100';
const NAME_LINK = 'p-0 text-left font-semibold text-teal-800 underline decoration-teal-400 underline-offset-2 hover:text-teal-950 dark:text-teal-200 dark:hover:text-white';
const KIND_TONE = {
  [ENCOUNTER_KIND.OPD]: 'bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-200',
  [ENCOUNTER_KIND.IPD]: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
};
const LINE_TONE = {
  error: 'text-red-700 dark:text-red-300',
  warn: 'text-amber-800 dark:text-amber-300',
};

// Codes with a decided FE sentence; any other code shows the server's own message.
const ERROR_TEXT = {
  ABHA_NOT_VERIFIED: LINK_COPY.notVerified,
  RECORD_BEFORE_ABHA_LINK: LINK_COPY.beforeAbha,
  DISCHARGE_SUMMARY_NOT_WRITTEN: LINK_COPY.noSummary,
  [ADMISSION_ACCESS_ERROR_CODE.OUT_OF_DEPARTMENT_SCOPE]: OUT_OF_DEPARTMENT_COPY_OTHER_ROLES,
  // OR-79: a slot sent while it would be an empty record (422 whole request, or a per-slot result): our words, not the server's.
  [SLOT_BLOCKED_REASON.RX_REQUIRED]: ROW.emptyRecord,
};
// NETWORK_ERROR's message is the API client's internal default, not for users. INCOMPLETE_RECORD names the missing
// field and where to fix it (one shared sentence, also used by both drawers).
const errorText = (code, message, missing) => {
  if (code === INCOMPLETE_RECORD) return incompleteRecordText(missing, message);
  return ERROR_TEXT[code] || (!message || code === 'NETWORK_ERROR' ? LINK_COPY.retryError : message);
};

const joinParts = (parts) => parts.filter((p) => p !== null && p !== undefined && p !== '').join(' · ');

// The card's ONE state pill (verified cards; null = nothing held yet). A blocked slot (a prescription not approved yet,
// OR-55; an empty OP Consultation, OR-79) is not counted. "Not linked yet" is isNotLinkedYet (hipRecordEnums), the same
// rule the server's NOT_LINKED_YET filter and count use (OR-86): a READY invoice draft or an unsaved bill suggestion
// counts, a row whose only gap is the empty OP Consultation does not. A PAUSED slot counts too (OR-104), but Failed / Paused outrank it for the pill's words.
// A LINKING slot counts for the LIST (OR-113: the card stays in "Not linked yet" until LINKED), but the pill keeps its
// words as before: "Linking…" when LINKING is all that is open, "Not linked yet" when a READY slot / suggestion is open too.
function cardState(allSlots, suggestionCount) {
  const slots = allSlots.filter((s) => !isBlocked(s));
  const has = (state) => slots.some((s) => s.state === state);
  if (has(SLOT_STATE.LINK_FAILED)) return { tone: 'bad', text: ROW.failed };
  if (has(SLOT_STATE.PAUSED)) return { tone: 'warn', text: SLOT_STATE_LABEL.PAUSED };
  if (isNotLinkedYet(allSlots.filter((s) => s.state !== SLOT_STATE.LINKING), suggestionCount)) return { tone: 'info', text: ROW.notLinkedYet };
  if (has(SLOT_STATE.LINKING)) return { tone: 'warn', text: ROW.linking };
  if (slots.length && slots.every((s) => s.state === SLOT_STATE.LINKED)) return { tone: 'ok', text: CARD.allLinkedPill, allLinked: true };
  return null;
}

export default function HipEncounterCard({
  record, onChanged, inPatientView = false, listFilters,
}) {
  const navigate = useNavigate();
  const canVerifyAbha = usePermission('ABHA_VERIFY');

  const [patched, setPatched] = useState({ base: null, slots: NO_PATCH }); // applied slots, by slotKey
  const [unticked, setUnticked] = useState(NO_KEYS); // slotKeys the user cleared (INV-S)
  const [slotErrors, setSlotErrors] = useState(NO_PATCH); // slotKey -> text, from the last link action
  const [notice, setNotice] = useState(null); // { tone, text } for a whole-request failure
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false); // link / delete requests: one at a time per card (one HIL start per action, I6)
  const [confirm, setConfirm] = useState(null); // { slots, picker } awaiting the Link confirm
  const [practitionerId, setPractitionerId] = useState(''); // the "Doctor for patient" picked in that confirm (OR-59)
  const [showLinked, setShowLinked] = useState(false); // a fully linked card's rows (collapsed by default)
  const [view, setView] = useState(null); // { slotKey, index }
  const [draft, setDraft] = useState(null); // the open inline Add file / Replace panel (hipUploadFiles.newDraft); one per card
  const [announce, setAnnounce] = useState(''); // screen-reader line (polite live region)
  const [focusRow, setFocusRow] = useState(null); // slotKey whose "View" link takes focus once the rows are drawn
  const articleRef = useRef(null);
  const panelRef = useRef(null);
  const fileInput = useRef(null);
  const pickFor = useRef(null); // what the open file picker is for: { mode, recordType, slotKey? } or { append: true, recordType }
  const opener = useRef(null); // the pill that opened the panel: Cancel / Esc give it focus back
  const panelId = `${useId()}-add-file`;
  const [invoiceDrawer, setInvoiceDrawer] = useState(null); // { invoice, suggestion, slotKey } - no invoice and no suggestion = a blank form
  const [deleting, setDeleting] = useState(null); // the draft invoice slot awaiting the delete confirm

  const { encounterKind, encounterId } = record;
  const patch = patched.base === record ? patched.slots : NO_PATCH;

  // Only the types that fit this kind, with the applied slots on top; a slot the list did not have yet (a fresh
  // upload, e.g. a new Diagnostic Report) joins after the slots of its type (stable sort by HI_TYPES order). An
  // Invoice fits both kinds: it is not a file type, so the server's `allowed_types` never lists it.
  // OR-87: on a visit with no doctor (the OR-59 signal, `needsPractitioner`: lab walk-in, nurse-only) the empty OP Consultation
  // (RX_REQUIRED) has no row at all - dropped here, once, so the rows, "Show n more" and "Select all" count the same rows. The
  // strip keeps its OP Consultation pill (a missing type either way: Add file); a staff file makes the slot linkable and it comes back.
  const slots = useMemo(() => {
    const fits = (s) => (s.recordType === TYPE_INVOICE || record.allowedTypes.includes(s.recordType))
      && !(record.needsPractitioner && isEmptyRecord(s));
    const known = new Set(record.slots.map((s) => s.slotKey));
    const merged = record.slots.map((s) => patch[s.slotKey] || s);
    const added = Object.values(patch).filter((s) => !known.has(s.slotKey));
    const all = added.length
      ? [...merged, ...added].sort((a, b) => HI_TYPES.indexOf(a.recordType) - HI_TYPES.indexOf(b.recordType))
      : merged;
    return all.filter(fits);
  }, [record, patch]);
  const suggestions = record.invoiceSuggestions ?? NO_SUGGESTIONS;
  const displayRecord = useMemo(() => ({ ...record, slots }), [record, slots]);
  const slotOf = (slotKey) => slots.find((s) => s.slotKey === slotKey);

  const patchSlots = (updates) => setPatched((prev) => {
    const next = { ...(prev.base === record ? prev.slots : {}) };
    Object.entries(updates).forEach(([slotKey, slot]) => {
      if (slot) next[slotKey] = slot; else delete next[slotKey];
    });
    return { base: record, slots: next };
  });

  const isIpd = encounterKind === ENCOUNTER_KIND.IPD;
  const verified = record.abhaStatus === ABHA_STATUS.VERIFIED;
  const needsPractitioner = !!record.needsPractitioner;
  // INV-S: one predicate (shared with the rows' tick box) -> the box, the button's N and the request never disagree.
  const tickable = useMemo(() => (verified ? slots.filter(isTickable) : []), [slots, verified]);
  const ticked = useMemo(() => tickable.filter((s) => !unticked.has(s.slotKey)), [tickable, unticked]);
  const tickedKeys = useMemo(() => new Set(ticked.map((s) => s.slotKey)), [ticked]);

  const toggle = (slot) => setUnticked((prev) => {
    const next = new Set(prev);
    if (next.has(slot.slotKey)) next.delete(slot.slotKey); else next.add(slot.slotKey);
    return next;
  });
  const allTicked = tickable.length > 0 && ticked.length === tickable.length;
  const toggleAll = () => setUnticked(allTicked ? new Set(tickable.map((s) => s.slotKey)) : NO_KEYS);

  const phrase = encounterPhrase({
    kind: encounterKind,
    patientName: record.patientName,
    dateText: record.visitDate ? formatDayIST(record.visitDate) : formatDateIST(record.encounterAt),
    ipNumber: record.ipNumber,
  });

  // ── Invoices: open the form / delete a draft ────────────────────────
  // The form gets the row's own slot (`slotOf`, by key, read live: the very object the row reads, so a refetch moves both together):
  // while it is PAUSED the form's Link / Retry link is off and says what the row says (OR-108). A suggestion / blank form has no slot.
  const openInvoice = (slot) => setInvoiceDrawer({ invoice: slot.invoice, suggestion: null, slotKey: slot.slotKey });
  const openSuggestion = (suggestion) => setInvoiceDrawer({ invoice: null, suggestion });

  // One delete request, and never alongside a link request: the card is busy meanwhile.
  const confirmDelete = async () => {
    const slot = deleting;
    setDeleting(null);
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setNotice(null);
    try {
      await deleteInvoice({ invoiceId: slot.invoice.invoiceId });
      onChanged();
    } catch (err) {
      // 409 INVOICE_LOCKED: it was linked meanwhile - the server says so; refresh to show the truth.
      const locked = err.status === 409;
      setNotice({ tone: 'error', text: `${slotName(slot)}: ${locked && err.userMessage ? err.userMessage : INVOICE.card.deleteFailed}` });
      if (locked) onChanged();
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  // ── Link / retry ────────────────────────────────────────────────────
  // EVERY write reports through here (OR-114): a link's records get this card's encounter (the list tracks a record by
  // encounterKind + encounterId + slotKey, or invoiceId) and its patient (OR-117: the list asks that patient's records when the
  // card is no longer in its rows), any other write has no argument.
  const changed = (linkInfo) => (linkInfo
    ? onChanged({ ...linkInfo, records: linkInfo.records.map((r) => ({ encounterKind, encounterId, patientId: record.patientId, ...r })) })
    : onChanged());

  // ONE records request carries every slotKey of `list` (one HIL start, I6). `doctorId` = the "Doctor for patient"
  // picked for a visit with no doctor (else undefined).
  const run = async (list, doctorId) => {
    if (inFlight.current || list.length === 0) return;
    inFlight.current = true;
    setBusy(true);
    setNotice(null);
    setSlotErrors(NO_PATCH);
    const before = Object.fromEntries(list.map((s) => [s.slotKey, s]));
    const undo = Object.fromEntries(list.map((s) => [s.slotKey, null]));
    // Show "Linking…" at once on exactly these slots; undone below for any that did not go through.
    patchSlots(Object.fromEntries(list.map((s) => [
      s.slotKey, { ...s, state: SLOT_STATE.LINKING, canLink: false, canReplace: false },
    ])));
    try {
      const { results } = await linkRecords({
        encounterKind, encounterId, slotKeys: list.map((s) => s.slotKey), practitionerId: doctorId,
      });
      const applied = {};
      const errors = {};
      results.forEach((r) => {
        if (r.ok) {
          applied[r.slotKey] = {
            ...before[r.slotKey],
            careContextId: r.careContextId ?? before[r.slotKey].careContextId,
            state: r.state || SLOT_STATE.LINKING,
            canLink: false,
            canReplace: false,
          };
        } else {
          errors[r.slotKey] = errorText(r.error?.code, r.error?.message);
        }
      });
      patchSlots({ ...undo, ...applied });
      setSlotErrors(errors);
      const linked = list.filter((s) => applied[s.slotKey]) // the rows' order
        .map((s) => ({ slotKey: s.slotKey, name: slotName(s), state: applied[s.slotKey].state }));
      changed(linked.length ? { patientName: record.patientName, records: linked } : undefined);
    } catch (err) {
      patchSlots(undo);
      if (err.code === PRACTITIONER_REQUIRED && needsPractitioner && !doctorId) {
        // A retry sent no doctor and the server has none stored (e.g. a crash before the care context was saved):
        // ask now, once. A request that already carried a doctor never loops back here.
        askToLink(list, true);
        return;
      }
      setNotice(err.code === LINK_BLOCKED && err.retryAfter
        ? { tone: 'warn', text: CARD.pausedNote(pausedWhenIST(Date.now() + Number(err.retryAfter) * 1000), err.pauseReason) }
        : { tone: 'error', text: errorText(err.code, err.userMessage, err.missing) });
      // OR-79: the server says a sent slot is an empty record - what the card shows is stale: refetch (the row turns grey).
      if (err.code === SLOT_BLOCKED_REASON.RX_REQUIRED) onChanged();
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  // A slot that already has a care context (a failed link) is a RETRY: it re-links with no doctor (R3).
  const isRetry = (slot) => slot.state === SLOT_STATE.LINK_FAILED || !!slot.careContextId;
  const allRetries = ticked.length > 0 && ticked.every(isRetry);
  // `picker`: does this confirm ask for the doctor? Yes on a no-doctor visit when any chosen slot is a new link.
  const askToLink = (chosen, picker = needsPractitioner && chosen.some((s) => !isRetry(s))) => {
    setPractitionerId(''); // each confirm starts empty: the doctor is picked on purpose, every time
    setConfirm({ slots: chosen, picker });
  };
  // The ONE button: every ticked row a retry -> at once, no confirm (as Retry link was); else confirm first.
  const linkTicked = () => (allRetries ? run(ticked) : askToLink(ticked));

  const confirmLink = () => {
    if (confirm.picker && !practitionerId) return;
    const { slots: chosen, picker } = confirm;
    setConfirm(null);
    run(chosen, picker ? practitionerId : undefined);
  };

  // ── Add file / Replace: the inline panel (OR-82) ────────────────────
  // Opens the OS file picker. Called straight from a click (no await before it: the browser only opens the picker from a
  // real click), and `multiple` is set on the input itself because a state update would come too late for this click.
  const openPicker = (target) => {
    pickFor.current = target;
    fileInput.current.multiple = !isSingleFile(target.recordType);
    fileInput.current.click();
  };
  // A strip pill (missing, or "+" on a multi type): pick the file(s) for that type; Invoice opens a blank bill form.
  // While a panel is open the strip locks its add pills itself (showFinishFirst), so this is never reached then.
  const addFromStrip = (recordType) => {
    if (recordType === TYPE_INVOICE) { setInvoiceDrawer({ invoice: null, suggestion: null }); return; }
    opener.current = articleRef.current.querySelector(`[data-type="${recordType}"] button`);
    openPicker({ mode: API_MODE.ADD, recordType });
  };
  // The pencil of the preview: pick the file that replaces this slot's. The preview stays open until a file is chosen.
  const replace = (slot) => {
    opener.current = null;
    openPicker({ mode: API_MODE.REPLACE, recordType: slot.recordType, slotKey: slot.slotKey });
  };
  // File(s) chosen: the first pick opens the panel (even with an error banner and no file), later picks add to it.
  const onPick = (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = ''; // picking the same file again must fire onChange
    const target = pickFor.current;
    pickFor.current = null;
    if (!picked.length || !target) return; // the picker was cancelled: nothing changes
    const base = target.append ? draft : (draft || newDraft(target));
    if (!base) return;
    const next = addPickedFiles(base, picked);
    setDraft(next);
    if (target.mode === API_MODE.REPLACE) setView(null);
    if (next.files.length > base.files.length) {
      setAnnounce(INLINE.chosenAnnounce(
        picked.map((f) => f.name).join(', '), hiTypeLabel(next.recordType), next.mode === API_MODE.REPLACE ? REPLACE.cta : UPLOAD.cta,
      ));
    }
  };

  const patchDraft = (patch) => setDraft((d) => d && { ...d, ...patch });
  const removeFile = (index) => setDraft((d) => d && { ...d, files: d.files.filter((_f, i) => i !== index), banner: null });
  // The panel stays; the user is told why a second add is not possible (and gets the panel back).
  const showFinishFirst = () => {
    patchDraft({ banner: { kind: BANNER.STATUS, text: INLINE.finishFirst } });
    if (panelRef.current) panelRef.current.focus();
  };
  const cancelDraft = () => {
    if (!draft || draft.phase !== PHASE.EDIT) return;
    if (draft.mode === API_MODE.REPLACE) setFocusRow(draft.slotKey);
    else if (opener.current) opener.current.focus();
    setDraft(null);
  };
  const rowsFocused = useCallback(() => setFocusRow(null), []);

  // The panel closes on success: the written slot is applied to the card at once, its row's "View" link takes focus.
  const finishDraft = (slot) => {
    patchSlots({ [slot.slotKey]: slot });
    setDraft(null);
    setFocusRow(slot.slotKey);
  };
  // The upload went through but the link did not: the panel closes into the saved row (ticked, "Link failed" if a care
  // context was made, else "Not linked yet") and the card line says why; the main button retries (R3: no doctor asked again).
  const linkFailed = (uploaded, text, careContextId, state) => {
    patchSlots({ [uploaded.slotKey]: { ...uploaded, careContextId: careContextId ?? uploaded.careContextId, state: state ?? uploaded.state } });
    setDraft(null);
    setFocusRow(uploaded.slotKey);
    setNotice({ tone: 'error', text: `${slotName(uploaded)}: ${UPLOAD.uploadedNote} ${text}` });
    onChanged();
  };
  // A failed upload (nothing was written). Where the words go depends on what went wrong (UPLOAD_ERROR_CODES).
  const uploadFailed = (err, d) => {
    if (UPLOAD_ERROR_CODES.CARD.has(err.code)) { // the card changed under us: say so on the card, refetch
      setDraft(null);
      setNotice({ tone: 'error', text: err.userMessage });
      onChanged();
      return;
    }
    let text = UPLOAD.uploadError;
    let files = d.files;
    if (UPLOAD_ERROR_CODES.FILE.has(err.code)) { // the file was refused: its sentence, the file is dropped, the typed name stays
      const name = err.fileName || (d.files.length === 1 ? d.files[0].name : '');
      text = err.code === FILE_REJECTED && name ? `${name}: ${err.userMessage}` : err.userMessage;
      files = err.fileName ? d.files.filter((f) => f.name !== err.fileName) : [];
    } else if (UPLOAD_ERROR_CODES.KEEP.has(err.code)) {
      text = err.userMessage;
    }
    patchDraft({ files, banner: { kind: BANNER.ERROR, text }, phase: PHASE.EDIT });
  };

  // Upload (ADD or REPLACE), then - ADD only - ONE link of the slot the upload returned. One request at a time per card.
  const submitDraft = async () => {
    if (inFlight.current || !draft) return;
    const d = draft;
    const replacing = d.mode === API_MODE.REPLACE;
    inFlight.current = true;
    setBusy(true);
    setNotice(null);
    patchDraft({ confirmOpen: false, banner: null, phase: replacing ? PHASE.REPLACING : PHASE.UPLOADING });
    let uploaded = null;
    try {
      ({ slot: uploaded } = await uploadRecordFiles({
        encounterKind,
        encounterId,
        recordType: d.recordType,
        mode: d.mode,
        slotKey: replacing ? d.slotKey : undefined, // ADD never names a slot (A1.4)
        title: draftTitle(d, record).trim() || undefined,
        files: d.files,
      }));
      if (replacing) { // Replace never links
        finishDraft(uploaded);
        setAnnounce(INLINE.replacedAnnounce(hiTypeLabel(d.recordType)));
        changed();
        return;
      }
      patchDraft({ phase: PHASE.LINKING });
      const { results } = await linkRecords({
        encounterKind,
        encounterId,
        slotKeys: [uploaded.slotKey],
        practitionerId: needsDoctor(d, record) ? d.practitionerId : undefined,
      });
      const r = results.find((x) => x.slotKey === uploaded.slotKey);
      if (r && r.ok) {
        finishDraft({
          ...uploaded, careContextId: r.careContextId, state: r.state ?? uploaded.state, canLink: false, canReplace: false,
        });
        changed({
          patientName: record.patientName,
          records: [{ slotKey: uploaded.slotKey, name: slotName(uploaded), state: r.state || SLOT_STATE.LINKING }],
        });
      } else {
        linkFailed(uploaded, errorText(r?.error?.code, r?.error?.message), r?.careContextId, r?.state);
      }
    } catch (err) {
      if (uploaded) linkFailed(uploaded, errorText(err.code, err.userMessage, err.missing));
      else uploadFailed(err, d);
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };
  // "Upload and link to ABHA" asks first (ASK_BEFORE_UPLOAD_AND_LINK); Replace is not a link, so it goes at once.
  const commitDraft = () => (draft.mode === API_MODE.ADD && ASK_BEFORE_UPLOAD_AND_LINK ? patchDraft({ confirmOpen: true }) : submitDraft());

  const viewAll = () => navigate(
    ROUTES.HIP_PATIENT_RECORDS.replace(':patientId', encodeURIComponent(record.patientId)),
    { state: { listFilters: listFilters || [] } },
  );

  // ── Render ───────────────────────────────────────────────────────────
  const state = verified ? cardState(slots, suggestions.length) : null;
  const collapsed = !!state?.allLinked && !showLinked;
  const lines = [];
  slots.forEach((s) => {
    if (!slotErrors[s.slotKey]) return;
    const label = slotName(s);
    lines.push({ key: s.slotKey, tone: 'error', text: slotErrors[s.slotKey] === LINK_COPY.retryError
      ? CARD.cardError(label) : `${label}: ${slotErrors[s.slotKey]}` });
  });
  if (notice) lines.push({ key: 'notice', ...notice });

  // The primary button's words: nothing linkable at all / none ticked (disabled, the reason IS the text) /
  // every ticked row a retry / "Link N records to ABHA".
  let primaryText = CARD.linkN(ticked.length);
  if (!tickable.length) primaryText = CARD.nothingToLink;
  else if (!ticked.length) primaryText = CARD.noneTicked;
  else if (allRetries) primaryText = CARD.tryAgain;

  const viewSlot = view && slotOf(view.slotKey);
  // A refetch can drop the slot an open preview points at: close it quietly.
  const viewGone = !!view && !viewSlot;
  // ... or drop / lock the slot an open Replace panel points at (linked meanwhile): the panel closes and the card says why.
  // Not while its own request runs (it ends by itself).
  const replaceSlot = draft && draft.mode === API_MODE.REPLACE ? slotOf(draft.slotKey) : null;
  const replaceGone = !!draft && draft.mode === API_MODE.REPLACE && draft.phase === PHASE.EDIT
    && (!replaceSlot || !replaceSlot.canReplace || !!replaceLockReason(replaceSlot));
  useEffect(() => {
    if (viewGone) setView(null);
    if (replaceGone) {
      setDraft(null);
      setNotice({ tone: 'error', text: ROWS.replaceLockedLinked });
    }
  }, [viewGone, replaceGone]);

  return (
    <article
      ref={articleRef}
      aria-label={`${record.patientName}, ${LIST.kinds[encounterKind]}`}
      className={`flex min-w-0 flex-col gap-2.5 rounded-[10px] border px-3.5 py-3 ${verified
        ? 'border-slate-300 bg-white dark:border-slate-600 dark:bg-slate-800'
        : 'border-slate-300 bg-slate-50 dark:border-slate-600 dark:bg-slate-700'}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-2.5">
        <div className="min-w-0 flex-[1_1_260px]">
          <p className="m-0 text-[13px] font-semibold text-slate-900 [overflow-wrap:anywhere] dark:text-slate-100">
            {inPatientView ? record.patientName : (
              <button type="button" className={NAME_LINK} title={PATIENT.viewAllLabel} onClick={viewAll}>{record.patientName}</button>
            )}
            {' '}
            <span className={`rounded px-1.5 py-0.5 text-[10.5px] font-extrabold tracking-wide ${KIND_TONE[encounterKind]}`}>
              {LIST.kinds[encounterKind]}
            </span>
            <span className="font-normal text-slate-600 dark:text-slate-300">{` · ${joinParts([record.uhid, record.ipNumber])}`}</span>
          </p>
          <p className="m-0 mt-0.5 text-xs text-slate-600 [overflow-wrap:anywhere] dark:text-slate-300">
            {joinParts([
              isIpd
                ? CARD.admittedDischarged(formatDateIST(record.admittedAt) || '—', formatDateIST(record.dischargedAt) || '—')
                : formatDateTimeISTWithYearNoSeconds(record.encounterAt) || formatDateIST(record.encounterAt) || '—',
              !isIpd && record.tokenNumber ? CARD.token(formatToken(record.tokenNumber, record.appointmentNumber, { hash: false })) : null,
              record.doctorName ? `Dr. ${record.doctorName.replace(/^dr\.?\s+/i, '')}` : null,
              record.departmentName,
            ])}
            {needsPractitioner && (
              <>
                {' · '}
                <span className="font-semibold text-amber-800 dark:text-amber-300">{PRACTITIONER.noDoctorNote}</span>
              </>
            )}
          </p>
        </div>
        {!verified && <span className={`${PILL} ${PILL_TONE.warn}`}>{CARD.notVerifiedChip}</span>}
        {state && <span className={`${PILL} ${PILL_TONE[state.tone]}`}>{state.text}</span>}
      </div>

      {!verified && <p className="m-0 text-xs text-slate-700 dark:text-slate-200">{CARD.notVerifiedHint}</p>}

      {verified && (
        <HipRecordTypeStrip
          record={record} slots={slots} disabled={busy} onAdd={addFromStrip}
          openType={draft && draft.mode === API_MODE.ADD ? draft.recordType : undefined} openPanelId={panelId}
          lockReason={draft ? INLINE.finishFirst : undefined} onLockedTap={showFinishFirst}
        />
      )}

      {verified && (
        // The one file picker of the card: hidden, no `capture` (OR-82: no camera button). The pill / pencil clicks it.
        <input
          ref={fileInput} type="file" hidden tabIndex={-1} accept={ACCEPT_ATTR} onChange={onPick}
          data-testid="hip-upload-file-input"
        />
      )}

      {verified && draft && (
        <HipInlineAddFile
          id={panelId} panelRef={panelRef} record={record} draft={draft} slot={replaceSlot}
          onTitle={(title) => patchDraft({ title })} onDoctor={(practitionerId) => patchDraft({ practitionerId })}
          onChooseFile={() => openPicker({ append: true, recordType: draft.recordType })}
          onRemoveFile={removeFile} onCancel={cancelDraft} onCommit={commitDraft}
        />
      )}

      <p className="sr-only" aria-live="polite">{announce}</p>

      {verified && tickable.length > 1 && slots.length + suggestions.length > ROW_CAP && (
        <label className="flex w-fit cursor-pointer items-center gap-2 text-xs font-semibold text-slate-800 dark:text-slate-100">
          <input
            type="checkbox" className="h-5 w-5 cursor-pointer accent-teal-600 disabled:cursor-not-allowed"
            checked={allTicked} disabled={busy} onChange={toggleAll}
          />
          {CARD.selectAll(tickable.length)}
        </label>
      )}

      {verified && !collapsed && (
        <HipRecordRows
          record={displayRecord}
          suggestions={suggestions}
          ticked={tickedKeys}
          onToggle={toggle}
          onView={(slot, index) => setView({ slotKey: slot.slotKey, index })}
          onOpenInvoice={openInvoice}
          onDeleteInvoice={setDeleting}
          onOpenSuggestion={openSuggestion}
          disabled={busy}
          focusSlotKey={focusRow}
          onFocused={rowsFocused}
        />
      )}

      {lines.map((l) => (
        <p key={l.key} role={l.tone === 'error' ? 'alert' : 'status'} className={`m-0 text-xs ${LINE_TONE[l.tone]}`}>{l.text}</p>
      ))}

      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        {!verified && canVerifyAbha && (
          <button type="button" className={BTN_PRIMARY} onClick={() => navigate(ROUTES.ABHA)}>{CARD.verifyAbha}</button>
        )}
        {verified && state?.allLinked && (
          <button type="button" className={LINK} aria-expanded={showLinked} onClick={() => setShowLinked((o) => !o)}>
            {showLinked ? CARD.hideRecords : CARD.showRecords}
          </button>
        )}
        {verified && !state?.allLinked && (
          <button type="button" className={BTN_PRIMARY} disabled={busy || !ticked.length} onClick={linkTicked}>
            {primaryText}
          </button>
        )}
        {!inPatientView && (
          <button type="button" className={LINK} onClick={viewAll}>{PATIENT.viewAll}</button>
        )}
      </div>

      {confirm && (
        <ConfirmationModal
          isOpen
          icon={ABDM_ICONS.LINK_RECORDS}
          title={confirm.slots.length > 1 ? CONFIRM.allTitle(confirm.slots.length) : CONFIRM.oneTitle}
          message={confirm.slots.length > 1 ? CONFIRM.allBodyShort(phrase) : CONFIRM.oneBodyShort(slotName(confirm.slots[0]), phrase)}
          ctaLabel={confirm.slots.length > 1 ? CONFIRM.allCta(confirm.slots.length) : CONFIRM.oneCta}
          cancelLabel={CONFIRM.cancel}
          onConfirm={confirmLink}
          onCancel={() => setConfirm(null)}
          ctaDisabled={confirm.picker && !practitionerId}
        >
          {confirm.slots.length > 1 && (
            // Scrolls inside the modal when many reports are listed.
            <ul className="mb-5 max-h-[40vh] list-none overflow-y-auto rounded-[10px] border border-slate-300 bg-slate-50 px-3 py-2 text-left text-[13px] text-slate-900 [overflow-wrap:anywhere] dark:border-slate-500 dark:bg-slate-700 dark:text-slate-100">
              {confirm.slots.map((s) => <li key={s.slotKey} className="py-0.5">{HI_TYPE_ICONS[s.recordType]} {slotName(s)}</li>)}
            </ul>
          )}
          {confirm.picker && confirm.slots.some(isRetry) && confirm.slots.some((s) => !isRetry(s)) && (
            <p className="mb-3 mt-0 text-left text-xs text-slate-600 dark:text-slate-300">{PRACTITIONER.keepsDoctor}</p>
          )}
          {confirm.picker && <HipPractitionerPicker value={practitionerId} onChange={setPractitionerId} />}
        </ConfirmationModal>
      )}

      {viewSlot && (
        <HipRecordPreviewModal
          open
          record={displayRecord}
          slot={viewSlot}
          initialIndex={view.index}
          onClose={() => setView(null)}
          onReplace={replace}
          replaceLock={draft ? INLINE.finishFirst : undefined}
        />
      )}

      {deleting && (
        <ConfirmationModal
          isOpen
          icon={null}
          variant="danger"
          autoFocusCancel
          title={INVOICE.card.deleteTitle}
          message={INVOICE.card.deleteBody(deleting.invoice.billNumber)}
          ctaLabel={INVOICE.card.deleteCta}
          cancelLabel={INVOICE.card.keepDraft}
          onConfirm={confirmDelete}
          onCancel={() => setDeleting(null)}
        />
      )}

      {invoiceDrawer && (
        <HipInvoiceDrawer
          open
          record={displayRecord}
          invoice={invoiceDrawer.invoice}
          suggestion={invoiceDrawer.suggestion}
          slot={slotOf(invoiceDrawer.slotKey)}
          onClose={() => setInvoiceDrawer(null)}
          onDone={(_invoice, linkInfo) => changed(linkInfo)}
        />
      )}

      {draft && draft.confirmOpen && (
        <ConfirmationModal
          isOpen
          icon={null}
          title={CONFIRM.oneTitle}
          message={CONFIRM.uploadBodyShort(hiTypeLabel(draft.recordType), phrase)}
          ctaLabel={CONFIRM.uploadCta}
          cancelLabel={CONFIRM.cancel}
          onConfirm={submitDraft}
          onCancel={() => patchDraft({ confirmOpen: false })}
        />
      )}
    </article>
  );
}
